import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F9F7F3]/85 backdrop-blur-md border-b border-[#E4DDCF] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <button
          data-testid="logo-button"
          onClick={() => handleNav("#hero")}
          className={`flex flex-col leading-none tracking-tight transition-colors ${
            scrolled ? "text-[#2D3728]" : "text-white"
          }`}
        >
          <span className="font-brand text-xl md:text-2xl">
            LAKE HOUSE
          </span>
          <span
            className={`text-[0.65rem] font-semibold uppercase tracking-[0.4em] ${
              scrolled ? "text-[#4A7055]" : "text-[#E8D9B5]"
            }`}
          >
            Juno · Mazury
          </span>
        </button>

        <div className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-${link.href.replace("#", "")}`}
              onClick={() => handleNav(link.href)}
              className={`text-sm font-medium transition-colors hover:text-[#4A7055] ${
                scrolled ? "text-[#2D3728]" : "text-white/90"
              }`}
            >
              {link.label}
            </button>
          ))}
          <a
            data-testid="navbar-reserve-button"
            href="#kontakt"
            onClick={(e) => {
              e.preventDefault();
              handleNav("#kontakt");
            }}
            className="inline-flex items-center gap-2 rounded-full bg-[#4A7055] px-5 py-2.5 text-sm font-semibold text-[#F9F7F3] shadow-lg shadow-[#4A7055]/20 transition-all hover:bg-[#3d5c47] hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" /> Rezerwuj
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${scrolled ? "text-[#2D3728]" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-[#F9F7F3] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                  onClick={() => handleNav(link.href)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-[#2D3728] hover:bg-[#EAE4D9]"
                >
                  {link.label}
                </button>
              ))}
              <a
                href={CONTACT.phoneHref}
                data-testid="mobile-reserve-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#4A7055] px-5 py-3 text-base font-semibold text-[#F9F7F3]"
              >
                <Phone className="h-4 w-4" /> Zadzwoń teraz
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
